// API service for fetching sales data
const API_BASE_URL = 'https://mzqhbqxno0.execute-api.ap-southeast-2.amazonaws.com';

/**
 * Fetch company sales data (The Jungle Group)
 * @returns {Promise} API response with company metrics
 */
export const fetchCompanySales = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/company-sales`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('📊 Company Sales API Response:', data);
    return data;
  } catch (error) {
    console.error('Error fetching company sales:', error);
    throw error;
  }
};

/**
 * Fetch seller sales data (TPS Nutrients)
 * @returns {Promise} API response with seller metrics
 */
export const fetchSellerSales = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/seller-sales`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('📊 Seller Sales API Response:', data);
    return data;
  } catch (error) {
    console.error('Error fetching seller sales:', error);
    throw error;
  }
};

/**
 * Fetch group sales data (TPS Nutrients and Bloom City brands)
 * @returns {Promise} API response with group metrics for each brand
 */
export const fetchGroupSales = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/group-sales`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('📊 Group Sales API Response:', data);
    return data;
  } catch (error) {
    console.error('Error fetching group sales:', error);
    throw error;
  }
};

/**
 * Transform API response to frontend data structure
 * Converts the single period data into week1 format for the existing table
 */
export const transformApiDataToFrontend = (apiResponse) => {
  if (!apiResponse || !apiResponse.metrics) {
    return null;
  }

  const { metrics } = apiResponse;
  
  // Transform to the format expected by the frontend (week1 format)
  return {
    totalSales: { week1: metrics.total_sales || 0 },
    unitsSold: { week1: metrics.units_sold || 0 },
    aov: { week1: metrics.aov || 0 },
    adSpend: { week1: metrics.ad_spend || 0 },
    totalFees: { week1: metrics.total_fees || 0 },
    tacos: { week1: metrics.tacos || 0 },
    profitPercent: { week1: metrics.profit_percentage || 0 },
    profitTotal: { week1: metrics.profit_total || 0 }
  };
};

/**
 * Fetch parent sales data (parent products with child breakdown)
 * @returns {Promise} API response with parent products and their children
 */
export const fetchParentSales = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/parent-sales`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('📊 Parent Sales API Response:', data);
    return data;
  } catch (error) {
    console.error('Error fetching parent sales:', error);
    throw error;
  }
};

/**
 * Transform groups API response to frontend structure
 */
export const transformGroupsDataToFrontend = (apiResponse) => {
  if (!apiResponse || !apiResponse.groups) {
    return {};
  }

  const { groups } = apiResponse;
  
  const transformedGroups = {};
  
  groups.forEach(group => {
    // Map brand names to group IDs
    // API returns label property (either "TPS Nutrients" or "Bloom City")
    const brandName = group.label || group.brand;
    const groupId = brandName === 'TPS Nutrients' ? 'tps-nutrients-group' : 'bloom-city';
    
    console.log(`🔄 Mapping brand "${brandName}" to group ID "${groupId}"`);
    
    transformedGroups[groupId] = {
      totalSales: { week1: group.metrics.total_sales || 0 },
      unitsSold: { week1: group.metrics.units_sold || 0 },
      aov: { week1: group.metrics.aov || 0 },
      adSpend: { week1: group.metrics.ad_spend || 0 },
      totalFees: { week1: group.metrics.total_fees || 0 },
      tacos: { week1: group.metrics.tacos || 0 },
      profitPercent: { week1: group.metrics.profit_percentage || 0 },
      profitTotal: { week1: group.metrics.profit_total || 0 }
    };
  });
  
  return transformedGroups;
};

/**
 * Transform parent sales API response to frontend structure
 * Converts parent products with nested children to the format expected by DataTable
 */
export const transformParentSalesToFrontend = (apiResponse) => {
  if (!apiResponse || !apiResponse.products) {
    return {};
  }

  const { products } = apiResponse;
  
  // Group products by brand
  const productsByBrand = {};
  
  products.forEach(product => {
    const brand = product.brand;
    if (!productsByBrand[brand]) {
      productsByBrand[brand] = [];
    }
    
    // Transform parent product to frontend format
    const transformedProduct = {
          brand: brand,
      product: product.product_name,
      sku: product.parent_asin,
      custom: product.product_name,
      stats: {
        // Default metrics
        totalSales: { week1: product.metrics.total_sales || 0 },
        unitsSold: { week1: product.metrics.units_sold || 0 },
        conversion: { week1: product.metrics.conversion || 0 },
        organicSales: { week1: product.metrics.organic_sales_pct || 0 },
        tacos: { week1: product.metrics.tacos || 0 },
        // Expanded metrics from API
        avgSalePrice: { week1: product.metrics.units_sold > 0 ? product.metrics.total_sales / product.metrics.units_sold : 0 },
        adImpressions: { week1: product.metrics.ad_impressions || 0 },
        adCpc: { week1: product.metrics.ad_cpc || 0 },
        adClicks: { week1: product.metrics.ad_clicks || 0 },
        adSales: { week1: product.metrics.ad_sales || 0 },
        adOrders: { week1: 0 }, // Not available in current data
        adSpend: { week1: product.metrics.ad_spend || 0 },
        adSpendPerUnit: { week1: product.metrics.ad_spend_per_unit || 0 },
        totalReferralFees: { week1: 0 }, // Not available in current data
        cogs: { week1: product.metrics.cogs || 0 },
        totalReferralFeesPercent: { week1: 0 }, // Not available in current data
        totalFbaFees: { week1: 0 }, // Not available in current data
        totalFees: { week1: product.metrics.total_fees || 0 },
        storageFees: { week1: 0 }, // Not available in current data
        inboundShippingFees: { week1: 0 }, // Not available in current data
        profitPerUnit: { week1: product.metrics.profit_per_unit || 0 },
        profitPercent: { week1: product.metrics.profit_percentage || 0 },
        profitTotal: { week1: product.metrics.profit_total || 0 },
        acos: { week1: product.metrics.acos || 0 },
        inventory: { week1: product.metrics.inventory || 0 }
      }
    };
    
    // Transform children if they exist
    if (product.metrics.children && product.metrics.children.length > 0) {
      console.log(`🔄 Transforming ${product.metrics.children.length} children for parent ${product.product_name}`);
      
      transformedProduct.variations = product.metrics.children.map((child, index) => {
        console.log(`  - Child ${index + 1}: ${child.size} (${child.child_asin}) - Sales: $${child.total_sales}`);
        
        return {
          brand: brand,
          product: `${product.product_name} - ${child.size}`,
          variation: child.size,
          sku: child.child_asin,
          custom: product.product_name,
          stats: {
            // Default metrics
            totalSales: { week1: child.total_sales || 0 },
            unitsSold: { week1: child.units_sold || 0 },
            conversion: { week1: child.conversion || 0 },
            organicSales: { week1: child.organic_sales_pct || 0 },
            tacos: { week1: child.tacos || 0 },
            // Expanded metrics from API
            avgSalePrice: { week1: child.units_sold > 0 ? child.total_sales / child.units_sold : 0 },
            adImpressions: { week1: child.ad_impressions || 0 },
            adCpc: { week1: child.ad_cpc || 0 },
            adClicks: { week1: child.ad_clicks || 0 },
            adSales: { week1: child.ad_sales || 0 },
            adOrders: { week1: 0 }, // Not available in current data
            adSpend: { week1: child.ad_spend || 0 },
            adSpendPerUnit: { week1: child.ad_spend_per_unit || 0 },
            totalReferralFees: { week1: 0 }, // Not available in current data
            cogs: { week1: child.cogs || 0 },
            totalReferralFeesPercent: { week1: 0 }, // Not available in current data
            totalFbaFees: { week1: 0 }, // Not available in current data
            totalFees: { week1: child.total_fees || 0 },
            storageFees: { week1: 0 }, // Not available in current data
            inboundShippingFees: { week1: 0 }, // Not available in current data
            profitPerUnit: { week1: child.profit_per_unit || 0 },
            profitPercent: { week1: child.profit_percentage || 0 },
            profitTotal: { week1: child.profit_total || 0 },
            acos: { week1: child.acos || 0 },
            inventory: { week1: child.inventory || 0 }
          }
        };
      });
      
      console.log(`✅ Transformed parent ${product.product_name} with ${transformedProduct.variations.length} variations`);
    } else {
      console.log(`⚠️ No children found for parent ${product.product_name}`);
    }
    
    productsByBrand[brand].push(transformedProduct);
  });
  
  // Summary logging
  const totalParents = Object.values(productsByBrand).reduce((sum, products) => sum + products.length, 0);
  const totalChildren = Object.values(productsByBrand).reduce((sum, products) => {
    return sum + products.reduce((childSum, product) => {
      return childSum + (product.variations ? product.variations.length : 0);
    }, 0);
  }, 0);
  
  console.log('✅ Transformed parent sales by brand:', productsByBrand);
  console.log(`📊 Summary: ${totalParents} parents with ${totalChildren} total children`);
  
  return productsByBrand;
};
